import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { buildShowroomSlug, fetchShowrooms, upsertShowroom } from "../lib/showrooms";
import { supabase } from "../supabaseClient";
import "./Showrooms.css";

const EMPTY_FORM = {
  showroom_name: "",
  brand: "",
  description: "",
  phone: "",
  whatsapp: "",
  email: "",
  website: "",
  city: "",
  state: "",
  area: "",
  full_address: "",
  latitude: "",
  longitude: "",
  google_map_url: "",
  profile_image: "",
  cover_image: "",
  gallery_text: "",
  opening_time: "09:00",
  closing_time: "18:00",
  sunday_open: false,
  services_text: "",
  verified: false,
  premium: false,
  status: "pending",
};

export default function ShowroomOwnerDashboard() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [showrooms, setShowrooms] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const ownerShowrooms = useMemo(() => showrooms.filter((s) => s.owner_id === user?.id), [showrooms, user?.id]);
  const activeShowroom = ownerShowrooms.find((s) => s.id === editingId) || null;

  const reload = async () => {
    const { data } = await fetchShowrooms({ limit: 100 });
    setShowrooms(data || []);
  };

  useEffect(() => {
    reload();
  }, []);

  if (!user) return <Navigate to="/login" replace />;

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const openEdit = (showroom) => {
    setEditingId(showroom.id);
    setForm({
      ...EMPTY_FORM,
      ...showroom,
      gallery_text: (showroom.gallery || []).join(", "),
      services_text: (showroom.services || []).join(", "),
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      owner_id: user.id,
      slug: form.slug || buildShowroomSlug(form.showroom_name, form.city, form.area),
      latitude: form.latitude ? Number(form.latitude) : null,
      longitude: form.longitude ? Number(form.longitude) : null,
      gallery: String(form.gallery_text || "").split(",").map((item) => item.trim()).filter(Boolean),
      services: String(form.services_text || "").split(",").map((item) => item.trim()).filter(Boolean),
    };
    const { error } = await upsertShowroom(payload);
    if (error) {
      setToast(error.message || "Unable to save showroom");
      setBusy(false);
      return;
    }
    setToast("Showroom saved");
    setEditingId(null);
    setForm(EMPTY_FORM);
    await reload();
    setBusy(false);
  };

  const deleteShowroom = async (id) => {
    if (!window.confirm("Delete this showroom permanently?")) return;
    await supabase.from("showrooms").delete().eq("id", id);
    await reload();
  };

  return (
    <div className="showrooms-page">
      <div className="showrooms-hero">
        <div>
          <p className="showrooms-kicker">Showroom Owner Dashboard</p>
          <h1>Manage your showroom listing</h1>
          <p>Create a listing, edit business details, update gallery links, and keep your presence current.</p>
        </div>
        <div className="showrooms-actions">
          <button type="button" className="showrooms-primary-btn" onClick={openCreate}>Add Showroom</button>
          {activeShowroom ? <button type="button" className="showrooms-secondary-btn" onClick={() => navigate(`/showroom/${activeShowroom.slug}`)}>Preview</button> : null}
        </div>
      </div>

      {toast ? <div className="showrooms-state">{toast}</div> : null}

      <form className="showrooms-owner-form" onSubmit={submit}>
        <div className="showrooms-filters">
          <input placeholder="Showroom name" value={form.showroom_name} onChange={(e) => setForm((p) => ({ ...p, showroom_name: e.target.value }))} required />
          <input placeholder="Brand" value={form.brand} onChange={(e) => setForm((p) => ({ ...p, brand: e.target.value }))} required />
          <input placeholder="City" value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} required />
          <input placeholder="State" value={form.state} onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))} required />
          <input placeholder="Area" value={form.area} onChange={(e) => setForm((p) => ({ ...p, area: e.target.value }))} />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
          <input placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => setForm((p) => ({ ...p, whatsapp: e.target.value }))} />
          <input placeholder="Email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          <input placeholder="Website" value={form.website} onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))} />
          <input placeholder="Google map URL" value={form.google_map_url} onChange={(e) => setForm((p) => ({ ...p, google_map_url: e.target.value }))} />
          <input placeholder="Profile image URL" value={form.profile_image} onChange={(e) => setForm((p) => ({ ...p, profile_image: e.target.value }))} />
          <input placeholder="Cover image URL" value={form.cover_image} onChange={(e) => setForm((p) => ({ ...p, cover_image: e.target.value }))} />
          <input placeholder="Latitude" value={form.latitude} onChange={(e) => setForm((p) => ({ ...p, latitude: e.target.value }))} />
          <input placeholder="Longitude" value={form.longitude} onChange={(e) => setForm((p) => ({ ...p, longitude: e.target.value }))} />
          <input placeholder="Gallery URLs separated by commas" value={form.gallery_text} onChange={(e) => setForm((p) => ({ ...p, gallery_text: e.target.value }))} />
          <input placeholder="Services separated by commas" value={form.services_text} onChange={(e) => setForm((p) => ({ ...p, services_text: e.target.value }))} />
          <input type="time" value={form.opening_time} onChange={(e) => setForm((p) => ({ ...p, opening_time: e.target.value }))} />
          <input type="time" value={form.closing_time} onChange={(e) => setForm((p) => ({ ...p, closing_time: e.target.value }))} />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
        </div>
        <div className="showroom-actions">
          <button type="submit" className="showrooms-primary-btn" disabled={busy}>{busy ? "Saving..." : editingId ? "Update showroom" : "Create showroom"}</button>
          <button type="button" className="showrooms-secondary-btn" onClick={() => setForm((p) => ({ ...p, sunday_open: !p.sunday_open }))}>{form.sunday_open ? "Sunday open" : "Sunday closed"}</button>
        </div>
      </form>

      <div className="showrooms-grid">
        {ownerShowrooms.length === 0 ? (
          <div className="showrooms-state">No listings yet. Create your first showroom above.</div>
        ) : (
          ownerShowrooms.map((showroom) => (
            <article key={showroom.id} className="showroom-card">
              <div className="showroom-card-body">
                <h3>{showroom.showroom_name}</h3>
                <p>{showroom.brand} · {showroom.city}, {showroom.state}</p>
                <p>Rating: {Number(showroom.average_rating || 0).toFixed(1)} | Reviews: {showroom.total_reviews || 0}</p>
                <div className="showroom-actions">
                  <button type="button" onClick={() => openEdit(showroom)}>Edit</button>
                  <button type="button" onClick={() => navigate(`/showroom/${showroom.slug}`)}>View</button>
                  <button type="button" onClick={() => deleteShowroom(showroom.id)}>Delete</button>
                </div>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
